import { DEFAULT_LOCALE, LOCALES, isAppLocale, type AppLocale } from "@examora/shared/i18n/locales";

/**
 * Interface-locale routing config. The locale list itself lives in
 * @examora/shared (the backend validates `uiLocale` against the same list);
 * this module adds the frontend-only concern of how it appears in URLs:
 * every page is served under a `/:locale` prefix (`localePrefix: "always"`),
 * and an unprefixed URL (e.g. a `/register?ref=CODE` referral link) is
 * redirected to the visitor's preferred locale — see detectPreferredLocale.
 */
export const routing = {
  locales: LOCALES,
  defaultLocale: DEFAULT_LOCALE,
} as const;

export type { AppLocale };
export { isAppLocale };

const LOCALE_STORAGE_KEY = "examora-locale";

/** Remembers the last locale the visitor actually used, for their next unprefixed visit. */
export function rememberLocale(locale: AppLocale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // storage unavailable (private mode) — detection just falls back to the browser language
  }
}

/**
 * Last-used locale → browser languages → default (Uzbek). An English browser is not taken as a
 * choice of English: phones in Uzbekistan mostly ship in English, so it sent Uzbek students the
 * English site and English verification emails. Russian and Turkish browsers keep their language,
 * and anyone who picks English in the switcher keeps it (it is remembered above).
 */
export function detectPreferredLocale(): AppLocale {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (isAppLocale(stored)) return stored;
  } catch {
    // ignore
  }
  for (const language of navigator.languages ?? [navigator.language]) {
    const base = language?.toLowerCase().split("-")[0];
    if (base !== "en" && isAppLocale(base)) return base;
  }
  return DEFAULT_LOCALE;
}

/** Splits `/uz/dashboard` into `{ locale: "uz", pathname: "/dashboard" }`. */
export function splitLocalePath(fullPath: string): { locale: AppLocale | null; pathname: string } {
  const match = fullPath.match(/^\/([^/]+)(\/.*)?$/);
  if (match && isAppLocale(match[1])) {
    return { locale: match[1], pathname: match[2] || "/" };
  }
  return { locale: null, pathname: fullPath || "/" };
}

/** Prefixes an app-internal path with a locale: ("/dashboard", "uz") → "/uz/dashboard". */
export function localizeHref(href: string, locale: AppLocale): string {
  if (!href.startsWith("/")) return href; // external, relative, or hash-only links pass through
  return href === "/" ? `/${locale}` : `/${locale}${href}`;
}
